// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import LeaseAgreementModal from "@guestsupply/App/Components/Cart/LeaseAgreementModal";
import toggleLeaseAgreement from "@guestsupply/App/Handlers/XNG/CartLeaseAgreement";
import { xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LoadingOverlay } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    /**
     * If true, displays an empty cart total UI.
     * If false, displays amounts from the cart.
     * Default value: false
     */
    isCartEmpty?: boolean;
    cart?: Cart;
    extendedStyles?: CartLeaseAgreementDisplayStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: xIsAuthenticated(state),
    isGuest: getSession(state).isGuest,
});
const mapDispatchToProps = {
    toggleLeaseAgreement,
};
type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface CartLeaseAgreementDisplayStyles {
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    wrapper?: InjectableCss;
    leaseAgreementLink?: LinkPresentationProps;
    leaseAgreementGridItem?: GridItemProps;
    leaseAgreementTypography?: TypographyProps;
    loadingOverlayStyle?: InjectableCss;
}

export const cartLeaseAgreementDisplayStyles: CartLeaseAgreementDisplayStyles = {};

const CartLeaseAgreementDisplay = ({
    isCartEmpty = false,
    cart,
    extendedStyles,
    // XNG-CHANGES
    isAuthenticated,
    isGuest,
    toggleLeaseAgreement,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(cartLeaseAgreementDisplayStyles, extendedStyles));
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLeaseLoading, setIsLeaseLoading] = useState(false);
    const handleModaleClose = () => {
        setIsModalOpen(false);
    };

    if (!cart || isCartEmpty) {
        return null;
    }

    const checkBoxChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setIsLeaseLoading(true);
        toggleLeaseAgreement({
            gpLeaseAccepted: `${value}`,
            onComplete: result => {
                setIsLeaseLoading(false);
            },
        });
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            {isAuthenticated &&
                !isGuest &&
                cart.properties["gpLeaseRequired"]?.toLowerCase() === "true" &&
                cart.properties["gpLeaseAccepted"]?.toLowerCase() === "true" && (
                    <GridContainer {...styles.container} data-test-selector={`leaseAgreement_${cart.id}`}>
                        <GridItem {...styles.titleGridItem}>
                            <Typography {...styles.titleText}>{translate("Lease Agreement")}</Typography>
                        </GridItem>
                        <GridItem width={12} {...styles.leaseAgreementGridItem}>
                            <Checkbox
                                checked={cart?.properties["gpLeaseAccepted"]?.toLowerCase() === "true"}
                                onChange={checkBoxChangeHandler}
                            >
                                <Typography
                                    {...styles.leaseAgreementTypography}
                                    data-test-selector="leaseAgreement_gpLeaseAccept"
                                >
                                    {translate("I have read and agree to the")}
                                    <Link {...styles.leaseAgreementLink} aria-label="Lease Agreement">
                                        {translate("Lease Agreement")}
                                    </Link>
                                </Typography>
                            </Checkbox>
                        </GridItem>
                        <LeaseAgreementModal isModalOpen={isModalOpen} handleModaleClose={handleModaleClose} />
                    </GridContainer>
                )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CartLeaseAgreementDisplay);
